import { ColumnDef } from "@tanstack/react-table";
import { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { ServerDeletedFilterValue } from "../../shared/ServerListFilters";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import ServerStatusPills from "../../shared/ServerStatusPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { newNetwork, Datum } from "../../interface/NewNetwork";
import { NewNetworkAxiosInstance } from "../../Network/NewNetworkAxiosInstance";
import { ScrollArea } from "../../components/ui/scroll-area";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import ListOfFacilityName from "../Shared/ListOfProviderName";
import { Option } from "../../interface/Option";
import FileUpload from "Pages/uploadFile/Network/Network";
import SpecialtyProviderSelector from "Pages/Shared/ProviderSpecialty";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

const NewNetwork: React.FC = () => {
  const [items, setItems] = useState<newNetwork>({ result: [] });
  const [provider, setProvider] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [description, setDescription] = useState<string>("");
  const [Name, setName] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [FacilityData, setFacilityData] = useState<Datum[]>([]);
  const [clearProvider, setclearProvider] = useState(false);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);

  const [selectedProviders, setSelectedProviders] = useState<{ Provider_id: string; Provider_name: string }[]>([]);
  const token = localStorage.getItem("token") || "";
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ✅ صلاحية الريستور والهارد دليت
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const [Encounter, setEncounter] = useState<"OP" | "IP" | "Both">("OP");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [advancedSearch, setAdvancedSearch] = useState({ search: "", status: "" });
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");

  // ─── View dialog ───────────────────────────────────────────────────────────
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);

  // ─── Force Dialog state ────────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);

  useEffect(() => { fetchUsers(); }, []);

  const clear = () => { setName(""); setDescription(""); setProvider([]); setIsActive(true); };

  const handleOpenDialog = (item: Datum) => {
    setSelectedItem(item);
    if (dialogRef.current) (dialogRef.current as any).showModal();
  };
const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Datum) => { handleEdit(item); setIsDialogOpen(true); };
  const handleFacilityChange = (selectedOptions: Option[]) => { setProvider(selectedOptions); };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ✅ Fetch — أحدث أول + المحذوفين آخر
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, {
    search: string;
    status: string;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { ...advancedSearch, deleted: filterDeleted },
    fetchPage: ({ page, limit, filters }) =>
      NewNetworkAxiosInstance.get("getNetwork", {
        params: {
          page,
          limit,
          active:
            filters.status === "Active"
              ? "true"
              : filters.status === "Inactive"
                ? "false"
                : undefined,
          is_deleted: filters.deleted,
        },
      }),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: (item: any) => ({
      ...item,
      status:
        item.status === true || item.status === "true" || item.status === "Active"
          ? "Active"
          : "Inactive",
    }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    isClientFallbackActive: (filters) =>
      filters.search.trim().length > 0,
    clientFilterRows: (rows, filters) => {
      const search = filters.search.toLowerCase().trim();
      if (!search) return rows;

      return rows.filter((row) =>
        String(row.id ?? "").toLowerCase().includes(search) ||
        String(row.network_name ?? "").toLowerCase().includes(search) ||
        row.provider?.some((provider) =>
          String(provider.Provider_id ?? "").toLowerCase().includes(search) ||
          String(provider.Provider_name ?? "").toLowerCase().includes(search)
        )
      );
    },
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch Network group", variant: "destructive" });
    },
  });

  // ✅ Toggle Status — مع force support
  const toggleNetworkStatus = async (item: Datum, force = false): Promise<void> => {
    try {
      const currentStatus = item.status === "Active" || item.status === true;
      const facilityValues = (item.provider || []).map((p) => ({ Provider_id: p.Provider_id || "", Provider_name: p.Provider_name || "" }));
      const url = force ? "updateNetwrok?force=true" : "updateNetwrok";
      const updatedItem = {
        data: { Network_name: item.network_name, provider: facilityValues, Status: !currentStatus ? "Active" : "Inactive", id: item.id },
        token,
      };
      const response = await NewNetworkAxiosInstance.put(url, updatedItem);
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceError("");
        toast({ title: "Success", description: `Status changed to ${!currentStatus ? "Active" : "Inactive"}.`, variant: "success" });
      } else {
        setForceId(item.id);
        setForceAction("status");
        setForceError(response.data.error || "This network is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const [nameError, setNameError] = useState<string>("");
  const [providersError, setProvidersError] = useState<string>("");
  const [gloableError, setGloableError] = useState<string>("");
  const existingNames = items.result.map((item) => item.network_name);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    if (!value.trim()) { setNameError("Name required"); return; }
    if (value.trim().length < 3) { setNameError("Name must be at least 3 characters long"); return; }
    const nameRegex = /^[A-Z][a-zA-Z\s]*$/;
    if (!nameRegex.test(value)) { setNameError("Invalid name. Only letters are allowed and must start with a capital letter."); return; }
    if (existingNames.includes(value.trim())) { setNameError("This name already exists"); return; }
    setNameError("");
  };

  const [isPatDialogOpen, setIsPatDialogOpen] = useState(false);
  const [Name2, setName2] = useState<string>("");
  const [ProviderID, setProviderID] = useState<string>("");
  const [Speciality, setSpeciality] = useState<string>("");
  const [isActive2, setIsActive2] = useState<boolean>(true);
  const [name2Error, setName2Error] = useState<string>("");
  const [providerError, setProviderError] = useState<string>("");
  const [specialityError, setSpecialityError] = useState<string>("");
  const [clearSpecialty, setClearSpecialty] = useState(false);
  const [specialty, setSpecialty] = useState<Option[]>([]);

  const handleSpecialtyChange = (specialties: Option[]) => { setSpecialty(specialties); };

  const handleName2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName2(value);
    if (!value.trim()) { setName2Error("Name required"); return; }
    if (value.trim().length < 3) { setName2Error("Name must be at least 3 characters long"); return; }
    setName2Error("");
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProviderID(value);
    setProviderError("");
    if (!value.trim()) return;
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    if (!alphanumericRegex.test(value)) { setProviderError("Provider ID must contain only letters and numbers"); return; }
  };

  const addItem2 = async (): Promise<void> => {
    try {
      setName2Error(""); setProviderError(""); setSpecialityError("");
      if (!Name2.trim()) { setName2Error("Provider Name is required."); toast({ title: "Error", description: "Provider Name is required.", variant: "destructive" }); return; }
      if (specialty.length === 0) { setSpecialityError("At least one Specialty must be selected."); toast({ title: "Error", description: "At least one Specialty must be selected.", variant: "destructive" }); return; }
      const finalProviderID = ProviderID.trim() || "";
      const ValuesSpecialty = specialty.map((option) => option.label);
      const newGroupArray = [{ Provider_name: Name2, Provider_id: finalProviderID, Speciality: ValuesSpecialty, Status: isActive2 ? "Active" : "Inactive", Encounter_checks: Encounter }];
      const payload = { data: newGroupArray, token };
      const response = await providerAxiosInstance.post("addNewProvider", payload);
      if (response.data.success) {
        fetchItems();
        const newOption: Option = { value: finalProviderID || `temp-${Date.now()}`, label: Name2.trim() };
        setProvider((prev) => { if (prev.some((p) => p.label === newOption.label)) return prev; return [...prev, newOption]; });
        setName2(""); setProviderID(""); setSpecialty([]); setIsActive2(true);
        setIsPatDialogOpen(false);
        toast({ title: "Success", description: "New Provider added successfully." });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to add provider.", variant: "destructive" });
    }
  };

  const addItem = async (): Promise<void> => {
    setNameError(""); setProvidersError(""); setGloableError("");
    let hasError = false;
    if (!Name.trim()) { setNameError("Name is required and cannot be empty."); hasError = true; }
    if (provider.length === 0) { setProvidersError("At least one Provider must be selected."); hasError = true; }
    if (hasError) { toast({ title: "Validation Error", description: "Please fill in all required fields.", variant: "destructive" }); return; }
    try {
      const facilityValues = provider.map(option => {
        const match = option.label?.match(/^\((.+?)\)\s*(.+)$/);
        if (match && match[1] && match[2]) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
        return { Provider_id: option.value?.toString() || "", Provider_name: option.label?.toString() || "" };
      });
      const newGroupArray = [{ Network_name: Name, provider: facilityValues, Status: isActive ? "Active" : "Inactive" }];
      const payload = { data: newGroupArray, token };
      const response = await NewNetworkAxiosInstance.post("addNewNetwork", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "New Network Group added successfully." });
      } else {
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach((err: any) => { setGloableError(err.error); toast({ title: "Error", description: err.error, variant: "destructive" }); });
        } else {
          toast({ title: "Error", description: response.data.error || "Failed to add the group.", variant: "destructive" });
        }
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
    setName(item.network_name);
    const facilitiesGroupData = Array.isArray(item.provider)
      ? item.provider.map((obj) => ({ value: obj.Provider_id, label: obj.Provider_name }))
      : [];
    setProvider(facilitiesGroupData);
    setIsActive(item.status);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    setNameError(""); setProvidersError("");
    let hasError = false;
    if (!Name.trim()) { setNameError("Name is required and cannot be empty."); hasError = true; }
    if (provider.length === 0) { setProvidersError("At least one Provider must be selected."); hasError = true; }
    if (hasError) { toast({ title: "Validation Error", description: "Please fill in all required fields.", variant: "destructive" }); return; }
    try {
      const facilityValues = provider.map(option => {
        const match = option.label?.match(/^\((.+?)\)\s*(.+)$/);
        if (match && match[1] && match[2]) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
        return { Provider_id: option.value?.toString() || "", Provider_name: option.label?.toString() || "" };
      });
      const updatedItem = { data: { Network_name: Name, provider: facilityValues, Status: isActive ? "Active" : "Inactive", id }, token };
      const response = await NewNetworkAxiosInstance.put("updateNetwrok", updatedItem);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Network group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Datum) => {
    const rows = ["name;provider;active"];
    const Provider = item.provider || [];
    const maxLen = Math.max(Provider.length);
    for (let index = 0; index < Math.max(1, maxLen); index++) {
      const provider = index < Provider.length ? Provider[index].Provider_name || "" : "";
      rows.push([item.network_name || "", provider, item.status ? "Active" : "Inactive"].join(";"));
    }
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Network${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  // ✅ Soft Delete
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await NewNetworkAxiosInstance.delete(`softDeleteNetwork/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Network deleted successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This network is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Network";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ✅ Restore
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await NewNetworkAxiosInstance.patch(`restoreNetwork/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Network restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore Network", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore Network", variant: "destructive" });
    }
  };

  // ✅ Hard Delete (permanent)
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteNetwork/${id}?force=true` : `deleteNetwork/${id}`;
      const response = await NewNetworkAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Network permanently deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This network is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete Network");
    }
  };

  // ✅ Force Soft Delete
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await NewNetworkAxiosInstance.delete(`softDeleteNetwork/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Network force deleted successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete Network");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete Network");
    }
  };

  const removeProviderFromNetwork = async (networkId: string | number, providerName: string): Promise<void> => {
    try {
      const currentNetwork = items.result.find(item => item.id === networkId);
      if (!currentNetwork) return;
      const updatedProviders = currentNetwork.provider?.filter(p => p.Provider_name !== providerName).map(p => p.Provider_name) || [];
      if (updatedProviders.length === 0) {
        toast({ title: "Error", description: "Cannot remove the last provider. Network must have at least one provider.", variant: "destructive" });
        return;
      }
      const updatedItem = { data: { Network_name: currentNetwork.network_name, provider: updatedProviders, Status: currentNetwork.status, id: networkId }, token };
      const response = await NewNetworkAxiosInstance.put("updateNetwrok", updatedItem);
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: `Provider "${providerName}" removed from network successfully.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to remove provider.", variant: "destructive" });
    }
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <span className="block text-center font-medium">{row.getValue("id")}</span>,
    },
    {
      accessorKey: "network_name",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Network Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = row.original.is_deleted;
        return (
          <span className={`block text-center font-medium ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("network_name")}
          </span>
        );
      },
    },
    {
      accessorKey: "provider",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Provider{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const providers = row.getValue("provider") as { Provider_id: string; Provider_name: string }[];
        const networkId = row.original.id;
        if (!providers || !Array.isArray(providers)) return <span className="text-gray-400 text-center block">-</span>;
        const searchTerm = advancedSearch.search.toLowerCase().trim();
        const hasSearch = searchTerm.length > 0;
        const filteredProviders = hasSearch ? providers.filter(p => p.Provider_name?.toLowerCase().includes(searchTerm)) : providers;
        const displayProviders = hasSearch && filteredProviders.length > 0 ? filteredProviders.slice(0, 2) : providers.slice(0, 2);
        const remainingCount = hasSearch ? filteredProviders.length - 2 : providers.length - 2;
        return (
          <div className="flex flex-col items-center gap-1">
            <div className="flex flex-wrap justify-center items-center gap-1.5">
              {displayProviders.map((provider, idx) => {
                const isMatching = hasSearch && provider.Provider_name?.toLowerCase().includes(searchTerm);
                return (
                  <div key={idx} className={`inline-flex items-center gap-1 rounded-full px-2 py-1 transition-all ${isMatching ? 'bg-yellow-100 border-2 border-yellow-500 shadow-md' : 'bg-blue-50'}`}>
                    <span className={`text-xs font-medium ${isMatching ? 'text-yellow-900 font-bold' : 'text-blue-800'}`}>
                      {isMatching && <i className="fa-solid fa-star text-yellow-600 mr-1"></i>}
                      {provider.Provider_name}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${provider.Provider_name}" from this network?`)) { removeProviderFromNetwork(networkId, provider.Provider_name); } }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-5 h-5 flex items-center justify-center transition-all" title="Remove from network">
                      <i className="fa-solid fa-times text-xs"></i>
                    </button>
                  </div>
                );
              })}
              {remainingCount > 0 && (
                <button onClick={() => { setSelectedProviders(hasSearch ? filteredProviders : providers); setProviderDialogOpen(true); }}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition-colors ${hasSearch ? 'bg-yellow-600 text-white hover:bg-yellow-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                  +{remainingCount} more {hasSearch && 'matching'}
                </button>
              )}
            </div>
            {hasSearch && filteredProviders.length > 0 && (
              <span className="text-xs text-yellow-700 font-medium mt-1">
                <i className="fa-solid fa-filter mr-1"></i>
                Showing {filteredProviders.length} of {providers.length} providers
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isActive = item.status === "Active" || item.status === true;
        const isDeleted = item.is_deleted;
        return (
          <div className="flex justify-center">
            <button
              onClick={() => !isDeleted && toggleNetworkStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : isActive ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
   {
  id: "view",
  header: () => <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white">View</Button>,
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="flex justify-center items-center gap-2">
        <button onClick={() => setSelectedItem(item)} className="text-blue-600 hover:text-blue-800" title="View Details">
          <i className="fa-solid fa-eye text-lg"></i>
        </button>
      </div>
    );
  },
},
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-network-wired"></i>
          </span>
          Network
        </h5>
      </div>

   <DataTable2
  columns={columns}
  data={items.result}
  searchValue={advancedSearch.search}
  onSearchChange={(value) => setAdvancedSearch((previous) => ({ ...previous, search: value }))}
  searchPlaceholder="Search..."
  searchHelpText="Network Name, Provider ID, or Provider Name."
  addButtonLabel="Add New Network"
  showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
  onEdit={handleEdit}
  onDelete={(id) => {
    const item = items.result.find(i => i.id === id);
    if ((item as any)?.is_deleted) {
      setForceId(id);
      setForceAction("hardDelete");
      setForceError("");
      setForceDialogOpen(true);
    } else {
      removeItem(id);
    }
  }}
  onRestore={canManageDeleted ? restoreItem : undefined}
  canHardDelete={canManageDeleted}
  onAdd={handleAddClick}
  pageIndex={currentPage}
  onPageChange={setCurrentPage}
  manualPagination={manualPagination}
  pageCount={totalPages}
  totalCount={totalCount}
  pageSize={pageSize}
  onPageSizeChange={(nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0);
  }}
  allowedPageSizes={allowedPageSizes}
  isLoading={isTableLoading}
  onEditClick={handleEditClick}
  onDownload={handleDownload}
  isRowDeleted={(row: any) => row.is_deleted === true}
  toolbarExtras={
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <ServerStatusPills
        value={
          advancedSearch.status === "Active"
            ? "true"
            : advancedSearch.status === "Inactive"
              ? "false"
              : ""
        }
        onChange={(value) => {
          setAdvancedSearch((previous) => ({
            ...previous,
            status: value === "true" ? "Active" : value === "false" ? "Inactive" : "",
          }));
          setCurrentPage(0);
        }}
      />
      <ServerDeletedPills
        value={filterDeleted}
        onChange={(value) => {
          setFilterDeleted(value);
          setCurrentPage(0);
        }}
      />
    </div>
  }
/>

      {/* View Dialog */}
{Boolean(selectedItem) && selectedItem && (
  <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
<DialogContent
  className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
  onInteractOutside={(event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(".list-of-provider-name-select__menu") ||
      target.closest(".list-of-provider-name-select__menu-portal")
    ) {
      event.preventDefault();
    }
  }}
  onPointerDownOutside={(event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(".list-of-provider-name-select__menu") ||
      target.closest(".list-of-provider-name-select__menu-portal")
    ) {
      event.preventDefault();
    }
  }}
  style={{
    boxShadow:
      "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "visible",
  }}
>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
            <i className="fa-solid fa-network-wired" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>{selectedItem.network_name}</h2>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: (selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: (selectedItem.status === "Active" || selectedItem.status === true) ? "#86efac" : "#fca5a5", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                {selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive"}
              </span>
              {(selectedItem as any).is_deleted && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                  <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                </span>
              )}
            </div>
            <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Network id: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
          </div>
        </div>
      </div>

      {/* Body */}
      <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
        <div style={{ padding: "20px 28px 16px" }}>

          {/* Basic Info */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
              <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: "#3b82f6" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic information</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Network name", value: selectedItem.network_name, icon: "fa-network-wired", color: "#3b82f6" },
              { label: "Status", value: selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive", icon: "fa-toggle-on", color: "#10b981" },
            ].map((field) => (
              <div key={field.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: `${field.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={`fa-solid ${field.icon}`} style={{ fontSize: 10, color: field.color }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize", letterSpacing: "0.05em" }}>{field.label}</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{field.value || "—"}</span>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

          {/* Providers */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
              <i className="fa-solid fa-hospital" style={{ fontSize: 12, color: "#3b82f6" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Assigned providers</span>
            <span style={{ fontSize: 10, padding: "2px 10px", borderRadius: 9999, background: "#3b82f6", color: "#fff", fontWeight: 700 }}>
              {selectedItem.provider?.length || 0}
            </span>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            {selectedItem.provider && selectedItem.provider.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, maxHeight: 220, overflowY: "auto" }}>
                {selectedItem.provider.map((p, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className="fa-solid fa-user-doctor" style={{ fontSize: 11, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{p.Provider_name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No providers assigned</span>
            )}
          </div>

          <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

          {/* Audit Trail */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created by</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                  {String(resolveUserName((selectedItem as any).created_by) ?? "?")[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created on</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last updated</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              </div>
            </div>
          </div>
{Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}

          <div style={{ paddingBottom: 8 }} />
        </div>
      </ScrollArea>

      {/* Footer */}
      <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
        <Button onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
          Close Details
        </Button>
      </div>
    </DialogContent>
  </Dialog>
)}

      {/* All Providers Dialog */}
      <Dialog open={providerDialogOpen} onOpenChange={setProviderDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list-check"></i>All Providers
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2">
                {selectedProviders && selectedProviders.map((provider, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-blue-100 hover:border-blue-300 transition-all">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0">
                      <i className="fa-solid fa-building text-sm"></i>
                    </div>
                    <span className="text-gray-800 font-medium flex-1">{provider.Provider_name}</span>
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${provider.Provider_name}" from this network?`)) { const networkId = items.result.find(net => net.provider?.some(p => p.Provider_name === provider.Provider_name))?.id; if (networkId) { removeProviderFromNetwork(networkId, provider.Provider_name); setProviderDialogOpen(false); } } }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-8 h-8 flex items-center justify-center transition-all" title="Remove from network">
                      <i className="fa-solid fa-trash-alt text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-blue-200">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-blue-600"></i>
                  <span>Total: <strong className="text-blue-900">{selectedProviders?.length || 0}</strong> providers</span>
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setProviderDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">
              <i className="fa-solid fa-check mr-2"></i>Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Network Dialog */}
  {/* Add/Edit Network Dialog */}
<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
  <DialogContent className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}>
    <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
          <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add Network" : "Edit Network"}</h2>
          <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Fill in the details below to {editIndex === null ? "create" : "update"} a Network</p>
        </div>
      </div>
    </div>

    <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
      <div style={{ padding: "22px 28px 32px" }}>
        {gloableError && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 18 }}>
            <i className="fa-solid fa-circle-exclamation" style={{ color: "#dc2626", marginTop: 2, fontSize: 12 }} />
            <p style={{ fontSize: 13, color: "#991b1b", margin: 0 }}>{gloableError}</p>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
          <Label htmlFor="name" className="text-xs font-semibold text-slate-500">Network Name</Label>
          <Input id="name" value={Name} onChange={handleNameChange} placeholder="Name is required" className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
          {nameError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
            <Label className="text-xs font-semibold text-slate-500">Status</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
              <button type="button" onClick={() => setIsActive(true)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive ? "#f0fdf4" : "#fff", color: isActive ? "#15803d" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
              </button>
              <button type="button" onClick={() => setIsActive(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive ? "#fef2f2" : "#fff", color: !isActive ? "#b91c1c" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
            <i className="fa-solid fa-hospital" style={{ fontSize: 11, color: "#9333ea" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Providers</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${providersError ? "#fca5a5" : "#e2e8f0"}`, padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
          <Label className="text-xs font-semibold text-slate-500">Assigned Providers</Label>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <div style={{ flex: 1 }}><ListOfFacilityName onChange={handleFacilityChange} defaultValue={provider} clear={clearProvider} /></div>
            <button type="button" onClick={() => setIsPatDialogOpen(true)} style={{ padding: "8px 14px", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              <i className="fa-solid fa-plus mr-1" /> New
            </button>
          </div>
          {providersError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{providersError}</p>}
        </div>
      </div>
    </ScrollArea>

    <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
      <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
      <Button onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
        <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
        {editIndex === null ? "Add Network" : "Update Network"}
      </Button>
    </div>
  </DialogContent>
</Dialog>

      {/* Add Provider Dialog */}
  {/* Add Provider Dialog */}
<Dialog open={isPatDialogOpen} onOpenChange={setIsPatDialogOpen}>
  <DialogContent className="max-w-[680px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}>
    <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
          <i className="fa-solid fa-user-plus" />
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>Add Provider</h2>
          <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Fill in the details below to create a Provider</p>
        </div>
      </div>
    </div>

    <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
      <div style={{ padding: "22px 28px 32px" }}>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="provider_name" className="text-xs font-semibold text-slate-500">Provider Name</Label>
              <Input id="provider_name" value={Name2} onChange={handleName2Change} placeholder="Name is required" className={`mt-1.5 ${name2Error ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
              {name2Error && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{name2Error}</p>}
            </div>
            <div>
              <Label htmlFor="provider_id" className="text-xs font-semibold text-slate-500">Provider ID</Label>
              <Input id="provider_id" value={ProviderID} onChange={handleIdChange} placeholder="Optional" className={`mt-1.5 ${providerError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
              {providerError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{providerError}</p>}
            </div>
          </div>

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
            <Label className="text-xs font-semibold text-slate-500">Status</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
              <button type="button" onClick={() => setIsActive2(true)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive2 ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive2 ? "#f0fdf4" : "#fff", color: isActive2 ? "#15803d" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
              </button>
              <button type="button" onClick={() => setIsActive2(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive2 ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive2 ? "#fef2f2" : "#fff", color: !isActive2 ? "#b91c1c" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
            <i className="fa-solid fa-list-check" style={{ fontSize: 11, color: "#9333ea" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Specialty</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${specialityError ? "#fca5a5" : "#e2e8f0"}`, padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
          <Label className="text-xs font-semibold text-slate-500">Select Specialties</Label>
          <div className="mt-1.5"><SpecialtyProviderSelector onChange={handleSpecialtyChange} clear={clearSpecialty} value={specialty} /></div>
          {specialityError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{specialityError}</p>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
            <i className="fa-solid fa-sliders" style={{ fontSize: 11, color: "#16a34a" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Encounter</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
          <Label className="text-xs font-semibold text-slate-500">Encounter Type</Label>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {(["OP", "IP", "Both"] as const).map((val) => (
              <button
                type="button"
                key={val}
                onClick={() => setEncounter(val)}
                style={{
                  flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: Encounter === val ? "1px solid #2563eb" : "1px solid #e2e8f0",
                  background: Encounter === val ? "#eff6ff" : "#fff",
                  color: Encounter === val ? "#1d4ed8" : "#64748b",
                }}
              >
                {val}
              </button>
            ))}
          </div>
        </div>
      </div>
    </ScrollArea>

    <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
      <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
      <Button onClick={() => addItem2()} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
        <i className="fa-solid fa-plus mr-2"></i>Save Provider
      </Button>
    </div>
  </DialogContent>
</Dialog>
      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the network.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                 <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
  <button
    onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
    style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
    Cancel
  </button>
  <button
    onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
    style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
    <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
  </button>
</div>
                </>
              )}

              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Network Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status") {
                          const item = items.result.find(i => i.id === forceId);
                          if (item) toggleNetworkStatus(item, true);
                        }
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default NewNetwork;